/**
 * Trial state — one cached read of /api/trial/status for every trial surface
 * (TRIAL_PLAN.md §8, Phase 5, Oct 2026).
 *
 * Every endpoint that changes a trial returns the same `trial` shape, so a
 * commit/revoke/cancel pushes the fresh state through `setTrial()` and the
 * ribbon, the billing card and the popups all repaint together.
 */
import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { getEntitlements, refreshEntitlements } from "@/lib/entitlements";

let _cache = null;
let _cacheUid = null;
let _inflight = null;
const _subs = new Set();

const _emit = () => { _subs.forEach((fn) => { try { fn(_cache); } catch { /* ignore */ } }); };

const _uid = () => { try { return getUser()?.id || null; } catch { return null; } };

export async function getTrial(force = false) {
  const uid = _uid();
  // Keyed by account on purpose: signing into a second account in the same tab
  // must never read the first one's trial back out of this cache.
  if (uid !== _cacheUid) { _cache = null; _queue = []; _cacheUid = uid; _qEmit(); }
  if (!uid) { _cache = null; _emit(); return null; }
  if (_cache && !force) return _cache;
  if (_inflight && !force) return _inflight;
  _inflight = api
    .get("/trial/status")
    .then((r) => { _cache = r.data || null; _cacheUid = uid; _emit(); return _cache; })
    .catch(() => null)
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function setTrial(next) { if (next) { _cache = next; _emit(); } }
export const refreshTrial = () => getTrial(true);

export function useTrial() {
  const [trial, setT] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  useEffect(() => {
    let alive = true;
    const onChange = (t) => { if (alive) setT(t); };
    _subs.add(onChange);
    getTrial().then((t) => { if (alive) { setT(t); setLoading(false); } });
    return () => { alive = false; _subs.delete(onChange); };
  }, []);
  return { trial, loading, refresh: refreshTrial };
}

/** Remember that a popup has been seen. Pure bookkeeping — moves no money. */
export async function ackTrial(kind, sku) {
  try {
    const r = await api.post("/trial/ack", { kind, sku: sku || null });
    setTrial(r.data?.trial);
    return r.data;
  } catch { return null; }
}

export const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export const trialDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
};

/* ── Creator Pro catalog, cached once (names + prices for the popups) ─────── */
let _catCache = null;
let _catInflight = null;

export async function getProCatalog() {
  if (_catCache) return _catCache;
  if (_catInflight) return _catInflight;
  _catInflight = api
    .get("/creator-pro/catalog")
    .then((r) => { _catCache = r.data?.catalog || null; return _catCache; })
    .catch(() => null)
    .finally(() => { _catInflight = null; });
  return _catInflight;
}

export function flattenCatalog(catalog) {
  const out = [];
  for (const suite of catalog?.suites || []) {
    for (const item of suite.items || []) {
      out.push({ ...item, suite: suite.name });
      for (const ch of item.children || []) out.push({ ...ch, suite: suite.name });
    }
  }
  return out;
}

/* ── First-use "keep this tool?" queue (§8.5) ─────────────────────────────────
   During a trial nothing is locked, so the prompt is driven by the tool
   actually being opened: each gated surface calls useTrialKeepPrompt(sku) on
   mount and the single app-level modal renders whatever is queued. One SKU at
   a time, once per SKU per account (the backend holds that list). */
let _queue = [];
const _qSubs = new Set();
const _qEmit = () => { _qSubs.forEach((fn) => { try { fn([..._queue]); } catch { /* ignore */ } }); };

export function queueKeepPrompt(sku) {
  if (!sku || _queue.includes(sku)) return;
  _queue = [..._queue, sku];
  _qEmit();
}

export function dequeueKeepPrompt(sku) {
  _queue = _queue.filter((s) => s !== sku);
  _qEmit();
}

export function useKeepPromptQueue() {
  const [q, setQ] = useState(_queue);
  useEffect(() => {
    const fn = (next) => setQ(next);
    _qSubs.add(fn);
    return () => { _qSubs.delete(fn); };
  }, []);
  return q;
}

/** Call from a gated surface: offers the tool for day 14 the first time a
 *  trial creator opens it. Never blocks, and never fires for a tool they
 *  already own, already committed, or have already been asked about. */
export function useTrialKeepPrompt(sku) {
  useEffect(() => {
    if (!sku) return undefined;
    let alive = true;
    (async () => {
      const t = await getTrial();
      if (!alive || !t?.live || t.cancel_at_period_end || t.commitments_frozen) return;
      if ((t.prompted_skus || []).includes(sku)) return;
      if ((t.committed_skus || []).some((r) => r.sku === sku)) return;
      let ent = await getEntitlements();
      // A second account in the same tab inherits the first one's cached
      // entitlements; the trial flag riding along in that payload is the tell.
      if (ent?.trial && !!ent.trial.live !== !!t.live) ent = await refreshEntitlements();
      if (!alive || ent?.owned_real?.[sku]) return;
      queueKeepPrompt(sku);
    })();
    return () => { alive = false; };
  }, [sku]);
}
